import React, { useEffect, useState } from "react";
import axios from "axios";
import { X, PartyPopper, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Welcome banner that appears when a discount code is auto-applied via URL param
 * (e.g. https://4keeperz.ch/?code=FCU15). The banner:
 *  - Verifies the code with the backend once (public endpoint)
 *  - Shows label + percentage
 *  - Dismisses on click of close button (per-code persistence)
 *  - Has a "Zum Shop" CTA that scrolls to products
 */
const AutoDiscountWelcome = () => {
  const cart = useCart();
  const { lang } = useLanguage();
  const [info, setInfo] = useState(null); // { label, percentage }

  useEffect(() => {
    if (!cart.autoCodeBanner) return;
    let cancelled = false;
    axios
      .post(`${API}/discount/validate`, { code: cart.autoCodeBanner })
      .then((res) => {
        if (!cancelled && res.data?.valid) {
          setInfo({
            label: res.data.label || cart.autoCodeBanner,
            percentage: res.data.percentage,
          });
        } else if (!cancelled) {
          // Silently dismiss invalid auto-applied codes
          cart.dismissAutoCodeBanner();
        }
      })
      .catch(() => {
        if (!cancelled) cart.dismissAutoCodeBanner();
      });
    return () => {
      cancelled = true;
    };
  }, [cart.autoCodeBanner, cart]);

  if (!cart.autoCodeBanner || !info) return null;

  const isDe = lang === "de";
  const t = {
    kicker: isDe ? "Willkommen · Rabatt aktiv" : "Welcome · Discount active",
    headline: isDe
      ? `${info.percentage}% Rabatt mit Code ${cart.autoCodeBanner}`
      : `${info.percentage}% off with code ${cart.autoCodeBanner}`,
    sub: info.label,
    cta: isDe ? "Zum Shop" : "Shop now",
    close: isDe ? "Schließen" : "Close",
  };

  const goToShop = () => {
    const el = document.getElementById("products");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      role="dialog"
      aria-live="polite"
      data-testid="auto-discount-welcome"
      className="fixed z-[70] left-1/2 -translate-x-1/2 bottom-6 md:bottom-8 w-[calc(100%-2rem)] max-w-md"
    >
      <div className="relative bg-black border-2 border-[#00A651] shadow-[0_25px_50px_-10px_rgba(0,166,81,0.4)] p-5 md:p-6">
        {/* Corner accent */}
        <div className="absolute -top-3 -left-3 w-6 h-6 bg-[#00A651] flex items-center justify-center">
          <PartyPopper className="w-3.5 h-3.5 text-black" />
        </div>

        <button
          type="button"
          onClick={cart.dismissAutoCodeBanner}
          aria-label={t.close}
          data-testid="auto-discount-welcome-close"
          className="absolute top-3 right-3 w-7 h-7 flex items-center justify-center text-white/50 hover:text-white transition-colors"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="pr-6">
          <div className="text-[10px] text-[#00A651] font-condensed uppercase tracking-widest mb-2">
            {t.kicker}
          </div>
          <div className="font-brush text-2xl md:text-3xl text-white leading-tight">
            {t.headline}
          </div>
          <div className="text-white/60 text-sm mt-1">{t.sub}</div>

          <button
            type="button"
            onClick={goToShop}
            data-testid="auto-discount-welcome-cta"
            className="mt-4 inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008A43] text-black font-condensed uppercase tracking-widest text-xs px-4 py-2.5 transition-colors"
          >
            <ShoppingBag className="w-3.5 h-3.5" />
            {t.cta}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AutoDiscountWelcome;
